// src/components/ImageDetailPage.tsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import ContentPageHeader from './PageHeader';
import { imageOrigin } from '../utils/illustrationRoute';
import { fetchImageContext, fetchPaginatedImages, fetchPopularImages, fetchPublicSearch, ImageMetadata, PublicFacet, getYearIndex } from '../services/api';
import { searchFilters } from '../utils/publicSearch';
import { getConfig } from '../config';

import Magnifier from './Magnifier'; // Import the Magnifier component
import { useContent } from '../contexts/ContentContext';
import { motion } from 'framer-motion';
import { useScore } from '../hooks/useScore';
import { useTooltip } from '../contexts/TooltipContext';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { formatDateTime, getYearMonth, toEpochMs } from '../utils/time';
import FavoriteControls from './FavoriteControls';
import BookmarkCount from './BookmarkCount';
import Modal from './Modal';
import ShareModal from './ShareModal';
import { Copy, Share2 } from 'lucide-react';
import { useTagSearch } from '../contexts/TagSearchContext';
import { recordTagUse } from '../utils/tagHistory';
import RelatedImageLinks from './RelatedImageLinks';
import { openGuideHelp } from '../utils/guideHelp';
import { ImagePreviewIndex, imagePreviewSeed } from '../utils/imagePreview';
import DetailImage from './DetailImage';
import DetailRegion from './DetailRegion';

interface ImageDetailPageProps {
    year: string;
    month: string;
    imageHash: string;
}

interface ImageDetailContentProps extends ImageDetailPageProps {
    imageId: string;
    contextSearch: string;
    mode?: 'page' | 'modal';
    navigationHost?: HTMLElement | null;
    onNavigate?: (href: string, index?: ImagePreviewIndex) => void;
    previewIndex?: ImagePreviewIndex;
}

interface PublicImageMetadata {
    image_id?: number;
    created?: number | string;
    control?: string;
    prompt?: string;
    title?: string;
    model?: string;
    quality?: string;
    model_display?: string;
    major_workflow_controls?: Record<string, string>;
    facets?: Record<string, PublicFacet>;
    rating?: number | null;
}

const promptModalYamlBlock = (value: string): string => {
    const text = value || '--';
    return text
        .replace(/\r\n/g, '\n')
        .split('\n')
        .map(line => `  ${line}`)
        .join('\n');
};

const buildRenderedPromptYamlText = (prompt: string): string => {
    return ['rendered_prompt: |', promptModalYamlBlock(prompt)].join('\n');
};

const formatModelWithQuality = (modelName?: string | null, quality?: string | null): string | null => {
    const model = (modelName || '').trim();
    const normalizedQuality = (quality || '').trim().toLowerCase();
    if (!model || model === '--') return null;
    if (!normalizedQuality || normalizedQuality === '--') return model;
    if (!model.toLowerCase().startsWith('gpt-image')) return model;
    if (model.toLowerCase().includes(`(${normalizedQuality})`)) return model;
    return `${model} (${normalizedQuality})`;
};

const renderPromptYamlLine = (line: string): React.ReactNode => {
    if (!line.trim()) return line;

    const keyValueMatch = line.match(/^(\s*)(-\s*)?([^:#\n][^:\n]*?)(:\s*)(.*)$/);
    if (!keyValueMatch) {
        return <span className="text-emerald-100">{line}</span>;
    }

    const [, indent, listPrefix = '', key, separator, value] = keyValueMatch;
    return (
        <>
            {indent}
            {listPrefix && <span className="text-gray-500">{listPrefix}</span>}
            <span className="text-sky-300">{key}</span>
            <span className="text-gray-500">{separator}</span>
            {value ? <span className="text-emerald-100">{value}</span> : null}
        </>
    );
};

const renderPromptYamlText = (text: string): React.ReactNode => {
    const lines = text.split('\n');
    return lines.map((line, index) => (
        <React.Fragment key={`${index}-${line}`}>
            {renderPromptYamlLine(line)}
            {index < lines.length - 1 ? '\n' : null}
        </React.Fragment>
    ));
};

export const ImageDetailContent: React.FC<ImageDetailContentProps> = ({ year, month, imageHash, imageId: routeImageId, contextSearch, mode = 'page', navigationHost, onNavigate, previewIndex }) => {
    const isPage = mode === 'page';
    const [attempt, setAttempt] = useState(0);
    const { apiBaseUrl } = getConfig();
    const apiOrigin = new URL(apiBaseUrl).origin;
    const [image, setImage] = useState<ImageMetadata | null>(null);
    const [metaRevisedPrompt, setMetaRevisedPrompt] = useState<string | null>(null);
    const [metaModelName, setMetaModelName] = useState<string | null>(null);
    const [metaPublishModelName, setMetaPublishModelName] = useState<string | null>(null);
    const [metaPublishVersion, setMetaPublishVersion] = useState<number>(0);
    const [metaPublicMetadata, setMetaPublicMetadata] = useState<PublicImageMetadata | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [pageItems, setPageItems] = useState<ImageMetadata[]>([]);
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [targetPage, setTargetPage] = useState<number | null>(null);
    const [targetGlobalIndex, setTargetGlobalIndex] = useState<number | null>(null);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [isMagnifierOpen, setIsMagnifierOpen] = useState(false);
    const [detailPanel, setDetailPanel] = useState<'data' | 'prompt' | null>(null);
    const promptButton = useRef<HTMLButtonElement>(null);
    const promptContent = useRef<HTMLPreElement>(null);
    const previousDetailPanel = useRef<typeof detailPanel>(null);
    const [promptCopyState, setPromptCopyState] = useState<'idle' | 'pending' | 'copied' | 'error'>('idle');
    const promptCopyRequest = useRef({ version: 0 });
    useEffect(() => {
        const request = promptCopyRequest.current;
        request.version++;
        setPromptCopyState('idle');
        return () => { request.version++; };
    }, [detailPanel, imageHash, metaRevisedPrompt, image?.render_prompt, image?.revised_prompt]);
    useEffect(() => {
        if (detailPanel === 'data' && previousDetailPanel.current === 'prompt') promptButton.current?.focus();
        if (detailPanel === 'prompt') promptContent.current?.focus();
        previousDetailPanel.current = detailPanel;
    }, [detailPanel]);
    const [boundaryPrevImage, setBoundaryPrevImage] = useState<ImageMetadata | null>(null);
    const [boundaryNextImage, setBoundaryNextImage] = useState<ImageMetadata | null>(null);
    const { content, language, t } = useContent();
    const { sendAction } = useScore();
    const { showTooltip, hideTooltip } = useTooltip();
    const { setRelatedTags } = useTagSearch();

    // State for cross-month navigation
    const [hasNewerImages, setHasNewerImages] = useState(true);
    const [hasOlderImages, setHasOlderImages] = useState(true);
    const [monthBoundary, setMonthBoundary] = useState<{ prev: 'pending' | 'missing' | 'ready' | 'error'; next: 'pending' | 'missing' | 'ready' | 'error' }>({ prev: 'pending', next: 'pending' });

    const urlParams = new URLSearchParams(contextSearch);
    const word = urlParams.get('word');
    const color = urlParams.get('color');
    const tagsParam = urlParams.get('tags');
    const resolvedWord = word || (tagsParam ? tagsParam.split(',')[0] : null);
    const hasWordFilter = Boolean(resolvedWord);
    const hasColorFilter = Boolean(color);
    const hasTagsParam = Boolean(tagsParam);
    const origin = imageOrigin(urlParams);
    const originParams = new URLSearchParams(origin?.slice(2).split('#')[0] || '');
    const homePopularOrigin = !!origin?.startsWith('/?#preview-popular-home-');
    const popularOrigin = originParams.get('root') === 'popular' || homePopularOrigin || !!origin?.includes('#preview-popular-ai-');
    const recentOrigin = (originParams.get('page') === 'gallery' && !popularOrigin && !originParams.has('year') && !originParams.has('month'))
        || (originParams.get('page') === 'illustrations' && originParams.get('root') === 'recent')
        || !!origin?.includes('#preview-recent-');
    const hasSearchFilter = urlParams.get('search') === '1' || recentOrigin || popularOrigin;
    const searchOrigin = originParams.get('page') === 'search';
    const contextFilters = searchFilters(searchOrigin ? originParams : urlParams);
    if (!searchOrigin) { contextFilters.delete('year'); contextFilters.delete('month'); }
    const savedSearchFilters = contextFilters.toString();

    const getAdjacentMonth = useCallback((currentYear: number, currentMonth: number, direction: 'prev' | 'next') => {
        const date = new Date(currentYear, currentMonth - 1);
        if (direction === 'prev') {
            date.setMonth(date.getMonth() - 1);
        } else {
            date.setMonth(date.getMonth() + 1);
        }
        return {
            year: date.getFullYear().toString(),
            month: (date.getMonth() + 1).toString().padStart(2, '0'),
        };
    }, []);

    const displayName = image?.public_title || (image ? t('detail.name', { id: image.id }) : content.ui[language].imageDetail.title);
    const hasLongTitle = Array.from(displayName).length > 80;
    const pageTitle = t('common.pageTitle', { title: displayName, name: content.siteName });
    const pageModelDescription = metaPublicMetadata?.model_display && metaPublicMetadata.model_display !== '--'
        ? metaPublicMetadata.model_display
        : formatModelWithQuality(metaPublicMetadata?.model, metaPublicMetadata?.quality);
    const pageDescription = image
        ? image.public_description || (pageModelDescription ? t('detail.description', { title: displayName, model: pageModelDescription }) : displayName)
        : '';
    const provenanceNotice = t('detail.text1');

    usePageMetadata({
        enabled: isPage,
        title: pageTitle,
        description: pageDescription,
        image: image?.detail_normal ? new URL(image.detail_normal, apiOrigin).href : undefined,
        type: 'article'
    });

    const viewed = useRef<string | null>(null);
    useEffect(() => {
        if (image && viewed.current !== `${image.id}:${image.hash}`) {
            viewed.current = `${image.id}:${image.hash}`;
            sendAction(image.id.toString(), 'view');
        }
    }, [image, sendAction, year, month]);

    useEffect(() => {
        let isActive = true;
        const fetchImageData = async () => {
            setImage(null);
            setPageItems([]); setCurrentIndex(-1); setTargetGlobalIndex(null);
            setBoundaryPrevImage(null); setBoundaryNextImage(null);
            setHasNewerImages(false); setHasOlderImages(false);
            setMonthBoundary({ prev: 'pending', next: 'pending' });
            setDetailPanel(null);
            setIsShareOpen(false);
            setIsMagnifierOpen(false);
            setImageFailed(false);
            setTags([]);
            setTagsPending(true);
            setMetaRevisedPrompt(null);
            setMetaPublicMetadata(null);
            setMetaModelName(null);
            setMetaPublishModelName(null);
            setMetaPublishVersion(0);
            setError(null);
            try {
                const { thumbnailsPerPage } = getConfig();
                const contextType = hasColorFilter ? 'color' : (resolvedWord ? 'word' : 'month');
                const searchQuery = new URLSearchParams(savedSearchFilters);
                if (recentOrigin) searchQuery.set('recent', '1');
                if (homePopularOrigin) searchQuery.set('min_score', '0');
                searchQuery.set('hash', imageHash);
                searchQuery.set('per_page', String(thumbnailsPerPage));
                const result = popularOrigin ? await fetchPopularImages(searchQuery) : hasSearchFilter ? await fetchPublicSearch(searchQuery) : await fetchImageContext(
                    contextType,
                    {
                        y: year,
                        m: month,
                        w: resolvedWord || undefined,
                        c: color || undefined,
                        per_page: thumbnailsPerPage
                    },
                    { hash: imageHash }
                );

                if (!isActive) return;
                const context = result.context;
                if (!context?.target_item) {
                    setError('missing');
                    return;
                }

                setPageItems(result.items || []);
                setTargetPage(context.target_page ?? null);
                setTargetGlobalIndex(context.target_index ?? null);
                setTotalItems(result.pagination?.total_items ?? 0);

                const currentImage = context.target_item as ImageMetadata;
                if (currentImage.hash !== imageHash || String(currentImage.id) !== routeImageId ||
                    currentImage.is_published === 0 || (currentImage.is_discard !== undefined && currentImage.is_discard !== 0) ||
                    (toEpochMs(currentImage.published_at) ?? 0) > Date.now()) {
                    setError('missing');
                    return;
                }
                setImage(currentImage);
                setCurrentIndex(
                    typeof context.target_index_in_page === 'number'
                        ? context.target_index_in_page
                        : (result.items || []).findIndex(img => img.hash === imageHash)
                );
                setBoundaryPrevImage(context.prev ?? null);
                setBoundaryNextImage(context.next ?? null);

                if (hasWordFilter || hasColorFilter || hasSearchFilter) {
                    setHasNewerImages(false);
                    setHasOlderImages(false);
                }

                setMetaRevisedPrompt(null);
                setMetaModelName(null);
                setMetaPublishModelName(null);
                setMetaPublishVersion(0);
                setMetaPublicMetadata(null);
                setDetailPanel(null);

                // Fetch the revised prompt from the meta JSON
                const metaPath = (currentImage as any).meta;
                if (metaPath) {
                    try {
                        const { apiBaseUrl } = getConfig();
                        const metaUrl = `${new URL(apiBaseUrl).origin}${metaPath}`;
                        const response = await fetch(metaUrl);
                        if (!response.ok) {
                            throw new Error(`Failed to fetch meta.json from ${metaUrl}`);
                        }
                        const metaData = await response.json();
                        if (!isActive) return;
                        const promptText = metaData.db?.render_prompt || metaData.db?.revised_prompt;
                        if (promptText) {
                            setMetaRevisedPrompt(promptText);
                        }
                        if (metaData?.public_metadata) {
                            setMetaPublicMetadata(metaData.public_metadata);
                        }
                        if (metaData?.db?.versions) {
                            const publishVersion = metaData?.db?.publish_version ?? (currentImage as any)?.publish_version ?? 0;
                            setMetaPublishVersion(publishVersion);
                            const versions = Array.isArray(metaData.db.versions) ? metaData.db.versions : [];
                            const baseVersion = versions.find((v: any) => v.version_number === 0);
                            const publishInfo = versions.find((v: any) => v.version_number === publishVersion);
                            setMetaModelName(baseVersion?.model_name || null);
                            setMetaPublishModelName(publishInfo?.model_name || null);
                        }
                    } catch (metaErr) {
                        console.error("Failed to fetch or parse meta JSON:", metaErr);
                    }
                }
            } catch (err) {
                if (isActive) setError('fetch');
            }
        };

        const checkAdjacentMonths = async () => {
            if (hasWordFilter || hasColorFilter || hasSearchFilter) return;

            const currentYearNum = parseInt(year, 10);
            const currentMonthNum = parseInt(month, 10);

            // Check for newer images (for "Prev" button)
            const newer = getAdjacentMonth(currentYearNum, currentMonthNum, 'next');
            try {
                const yearIndex = await getYearIndex(newer.year);
                const monthExists = yearIndex.months.some(m => m.month === newer.month);
                if (isActive) { setHasNewerImages(monthExists); setMonthBoundary(value => ({ ...value, prev: monthExists ? 'pending' : 'missing' })); }
            } catch {
                if (isActive) { setHasNewerImages(false); setMonthBoundary(value => ({ ...value, prev: 'error' })); }
            }

            // Check for older images (for "Next" button)
            const older = getAdjacentMonth(currentYearNum, currentMonthNum, 'prev');
            try {
                const yearIndex = await getYearIndex(older.year);
                const monthExists = yearIndex.months.some(m => m.month === older.month);
                if (isActive) { setHasOlderImages(monthExists); setMonthBoundary(value => ({ ...value, next: monthExists ? 'pending' : 'missing' })); }
            } catch {
                if (isActive) { setHasOlderImages(false); setMonthBoundary(value => ({ ...value, next: 'error' })); }
            }
        };


        fetchImageData();
        checkAdjacentMonths();
        return () => { isActive = false; };
    }, [year, month, imageHash, routeImageId, getAdjacentMonth, word, color, resolvedWord, hasWordFilter, hasColorFilter, hasSearchFilter, savedSearchFilters, recentOrigin, popularOrigin, homePopularOrigin, isPage, attempt]);

    useEffect(() => {
        let isActive = true;

        const loadBoundaryImages = async () => {
            if (!image || image.hash !== imageHash || hasWordFilter || hasColorFilter || hasSearchFilter) {
                return;
            }

            if (targetGlobalIndex === null || totalItems === 0) {
                return;
            }

            const currentYearNum = parseInt(year, 10);
            const currentMonthNum = parseInt(month, 10);

            if (targetGlobalIndex <= 0 && hasNewerImages && !boundaryPrevImage) {
                try {
                    const newer = getAdjacentMonth(currentYearNum, currentMonthNum, 'next');
                    const newerResult = await fetchPaginatedImages('month', {
                        y: newer.year,
                        m: newer.month,
                        page: 999999,
                        per_page: 1
                    });
                    const lastImage = newerResult.items?.[0];
                    if (isActive && lastImage) {
                        setBoundaryPrevImage(lastImage);
                    }
                    if (isActive) setMonthBoundary(value => ({ ...value, prev: lastImage ? 'ready' : 'missing' }));
                } catch (err) {
                    console.error('Failed to load newer month thumbnail', err);
                    if (isActive) setMonthBoundary(value => ({ ...value, prev: 'error' }));
                }
            }

            if (targetGlobalIndex >= totalItems - 1 && hasOlderImages && !boundaryNextImage) {
                try {
                    const older = getAdjacentMonth(currentYearNum, currentMonthNum, 'prev');
                    const olderResult = await fetchPaginatedImages('month', {
                        y: older.year,
                        m: older.month,
                        page: 1,
                        per_page: 1
                    });
                    const firstImage = olderResult.items?.[0];
                    if (isActive && firstImage) {
                        setBoundaryNextImage(firstImage);
                    }
                    if (isActive) setMonthBoundary(value => ({ ...value, next: firstImage ? 'ready' : 'missing' }));
                } catch (err) {
                    console.error('Failed to load older month thumbnail', err);
                    if (isActive) setMonthBoundary(value => ({ ...value, next: 'error' }));
                }
            }
        };

        loadBoundaryImages();

        return () => {
            isActive = false;
        };
    }, [targetGlobalIndex, totalItems, hasNewerImages, hasOlderImages, hasWordFilter, hasColorFilter, hasSearchFilter, year, month, boundaryPrevImage, boundaryNextImage, getAdjacentMonth, image, imageHash]);

    const handleBackToList = async () => {
        const origin = imageOrigin(urlParams);
        if (origin) {
            const saved = window.history.state?.contentList;
            window.history.pushState(saved?.href === origin ? { restoreContentY: saved.scrollY } : {}, '', origin);
            window.dispatchEvent(new PopStateEvent('popstate'));
            return;
        }
        const navigateTo = (url: string) => {
            window.history.pushState({}, '', url);
            window.dispatchEvent(new PopStateEvent('popstate'));
        };

        if (urlParams.get('from') === 'favorites') {
            const params = new URLSearchParams({ page: 'favorites' });
            if (urlParams.get('collection')) params.set('collection', urlParams.get('collection')!);
            if (urlParams.get('unfiled') === '1') params.set('unfiled', '1');
            navigateTo('/?' + params);
            return;
        }

        if (hasSearchFilter) {
            const params = new URLSearchParams(savedSearchFilters);
            params.set('page', 'search');
            params.set('pageNumber', String(targetPage ?? 1));
            navigateTo(`/?${params}${image ? `#image-${image.id}` : ''}`);
            return;
        }
        if (hasColorFilter && color) {
            const pageNumber = targetPage ?? 1;
            const hash = image ? `#image-${image.id}` : '';
            const params = new URLSearchParams();
            params.set('page', 'color');
            params.set('color', color);
            params.set('pageNumber', pageNumber.toString());
            navigateTo(`/?${params.toString()}${hash}`);
            return;
        }

        if (hasWordFilter) {
            // Calculate page number for word gallery
            const pageNumber = targetPage ?? 1;
            // Add hash to scroll to the specific image if available
            const hash = image ? `#image-${image.id}` : '';
            const params = new URLSearchParams();
            params.set('page', 'word');
            if (hasTagsParam && tagsParam) {
                params.set('tags', tagsParam);
            } else if (resolvedWord) {
                params.set('word', resolvedWord);
            }
            params.set('pageNumber', pageNumber.toString());
            navigateTo(`/?${params.toString()}${hash}`);
            return;
        }

        try {
            const currentParams = new URLSearchParams(contextSearch);
            const currentYear = currentParams.get('year');
            const currentMonth = currentParams.get('month');

            if (!currentYear || !currentMonth) {
                navigateTo('/'); // Fallback to home
                return;
            }

            const pageNumber = targetPage ?? 1;
            const imageId = image?.id;
            const anchor = imageId ? `#image-${imageId}` : '';
            navigateTo(`/?page=gallery&year=${currentYear}&month=${currentMonth}&pageNumber=${pageNumber}${anchor}`);
        } catch (err) {
            console.error('Failed to calculate page number, returning to default view.', err);
            navigateTo('/');
        }
    };

    const navigateImage = (target: ImageMetadata | null) => {
        if (!target || !image || image.hash !== imageHash) return;
        const dateParts = getYearMonth(target.time);
        const params = new URLSearchParams(contextSearch);
        params.set('page', 'gallery'); params.set('view', 'image');
        params.set('year', dateParts?.year ?? year); params.set('month', dateParts?.month ?? month);
        params.set('id', String(target.id)); params.set('hash', target.hash);
        const url = '/?' + params;
        if (onNavigate) {
            const samePage = pageItems.some(item => item.id === target.id && item.hash === target.hash);
            onNavigate(url, { image: imagePreviewSeed(target), items: samePage ? pageItems.map(imagePreviewSeed) : undefined, page: samePage ? targetPage ?? undefined : undefined, crossPage: !samePage });
        }
        else { window.history.pushState({}, '', url); window.dispatchEvent(new PopStateEvent('popstate')); }
    };
    const currentImage = image?.hash === imageHash && String(image.id) === routeImageId && !error ? image : null;
    const prevCandidate = currentIndex > 0 ? pageItems[currentIndex - 1] : boundaryPrevImage;
    const nextCandidate = currentIndex < pageItems.length - 1 ? pageItems[currentIndex + 1] : boundaryNextImage;
    const prevImage = currentImage && urlParams.get('from') !== 'favorites' && prevCandidate?.id !== currentImage.id ? prevCandidate : null;
    const nextImage = currentImage && urlParams.get('from') !== 'favorites' && nextCandidate?.id !== currentImage.id ? nextCandidate : null;
    const prevDisabled = !prevImage;
    const nextDisabled = !nextImage;
    const filteredContext = hasWordFilter || hasColorFilter || hasSearchFilter;
    const prevState = prevImage ? 'ready' : !currentImage ? (error ? 'error' : 'pending') : urlParams.get('from') === 'favorites' ? 'missing'
        : targetGlobalIndex !== null && targetGlobalIndex > 0 ? 'error' : filteredContext ? 'missing' : monthBoundary.prev;
    const nextState = nextImage ? 'ready' : !currentImage ? (error ? 'error' : 'pending') : urlParams.get('from') === 'favorites' ? 'missing'
        : targetGlobalIndex !== null && targetGlobalIndex < totalItems - 1 ? 'error' : filteredContext ? 'missing' : monthBoundary.next;
    const handlePrev = () => navigateImage(prevImage);
    const handleNext = () => navigateImage(nextImage);
    const navigationCards = <>
        <button type="button" disabled={prevDisabled} data-navigation-state={prevState} onClick={handlePrev} title={prevDisabled ? t('detail.first') : undefined}>{prevImage?.thumb ? <img src={new URL(prevImage.thumb, apiOrigin).href} alt="" /> : !isPage && <span className="lab-nav-thumb" aria-hidden="true" />}{t('imageDetail.previous')}</button>
        <button type="button" disabled={nextDisabled} data-navigation-state={nextState} onClick={handleNext} title={nextDisabled ? t('detail.last') : undefined}>{nextImage?.thumb ? <img src={new URL(nextImage.thumb, apiOrigin).href} alt="" /> : !isPage && <span className="lab-nav-thumb" aria-hidden="true" />}{t('imageDetail.next')}</button>
    </>;
    const modalNavigation = !isPage && navigationHost ? createPortal(<nav className="lab-image-navigation" aria-label={t('imageDetail.title')}>{navigationCards}</nav>, navigationHost) : null;

    const [tags, setTags] = useState<{ word: string; count: number }[]>([]);
    const [tagsPending, setTagsPending] = useState(true);
    const [hoveredWord, setHoveredWord] = useState<string | null>(null);
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [imageFailed, setImageFailed] = useState(false);

    useEffect(() => {
        let active = true;
        if (image && image.hash === imageHash && String(image.id) === routeImageId) {
            const fetchTags = async () => {
                try {
                    const response = await fetch(`${apiBaseUrl}/index/tags/${year}/${month}.json?id=${image.id}`);
                    if (response.ok) {
                        const data = await response.json();
                        if (!active) return;
                        const imageTags = data[image.id] || [];
                        // Check if it's the old format (string[]) or new format ({word, count}[])
                        if (imageTags.length > 0 && typeof imageTags[0] === 'string') {
                            // Fallback for old format (shouldn't happen after regeneration but good for safety)
                            setTags(imageTags.map((t: string) => ({ word: t, count: 0 })));
                        } else {
                            setTags(imageTags);
                        }
                        const related = (imageTags || [])
                            .map((t: any) => (typeof t === 'string' ? t : t.word))
                            .filter((t: string) => t && t !== resolvedWord)
                            .slice(0, 12);
                        if (isPage) setRelatedTags(related);
                    }
                } catch (e) {
                    console.error("Failed to fetch tags", e);
                } finally {
                    if (active) setTagsPending(false);
                }
            };
            fetchTags();
        }
        return () => { active = false; };
    }, [image, imageHash, routeImageId, year, month, apiBaseUrl, resolvedWord, setRelatedTags, isPage]);

    if (error && isPage) {
        return <>{modalNavigation}{isPage && <ContentPageHeader sidebar actions={<a className="lab-button" href="/?page=gallery">{t('imageDetail.backToList')}</a>} />}<div role="alert" className="lab-error">{t('imageDetail.error')}<button className="lab-button" onClick={() => setAttempt(value => value + 1)}>{t('common.retry')}</button></div></>;
    }

    if (!image || !currentImage) {
        if (isPage) return <>{modalNavigation}<ContentPageHeader sidebar /><div role="status" className="dark:text-gray-300">{content.ui[language].imageDetail.loading}</div></>;
        const seed = previewIndex?.image.hash === imageHash && String(previewIndex.image.id) === routeImageId ? previewIndex.image : undefined;
        const title = seed?.public_title || t('detail.name', { id: routeImageId });
        return <>{modalNavigation}<div className="lab-page lab-detail lab-content-page" data-detail-id={routeImageId} aria-busy={!error}>
            {null}
            <div className="lg:flex lg:gap-6"><div className="min-w-0 flex-1 text-gray-900 dark:text-gray-100 ivory:text-coffee-800">
                <div className="lab-detail-columns"><div className="lab-detail-art">
                    <DetailImage identity={imageHash} thumb={seed?.thumb ? new URL(seed.thumb, apiOrigin).href : undefined} title={title} retry={attempt} waiting={!error && !!previewIndex?.crossPage} />
                </div><DetailRegion className="lab-detail-info" pending={!error} fallback={360}
                    preview={<><p className="lab-seed-title">{title}</p><p>{seed?.time !== undefined ? formatDateTime(seed.time) : ''}</p></>}>
                    <div className="lab-detail-actions"><span className="lab-detail-placeholder" aria-hidden="true" /></div>
                    <button disabled type="button" className="lab-button lab-detail-parameters">{t('detail.data')}</button>
                    <div className="lab-detail-placeholder" aria-hidden="true" />
                    <p className="lab-seed-title">{title}</p><p>{seed?.time !== undefined ? formatDateTime(seed.time) : ''}</p>
                    {error && <div role="alert" className="lab-error">{t('imageDetail.error')}<button className="lab-button" onClick={() => setAttempt(value => value + 1)}>{t('common.retry')}</button></div>}
                </DetailRegion></div>
                <DetailRegion className="lab-detail-tags" pending fallback={64}><span className="lab-detail-placeholder" aria-hidden="true" /></DetailRegion>
                <DetailRegion className="lab-detail-related" pending fallback={100}><span className="lab-detail-placeholder" aria-hidden="true" /></DetailRegion>
            </div></div>
        </div></>;
    }

    const imageUrl = new URL(image.detail_normal, apiOrigin).href;
    const highResImageUrl = new URL(image.detail_magnifier, apiOrigin).href;
    const shareUrl = `${window.location.origin}/ogp/images/${image.id}.html`;
    const generationPromptText = metaRevisedPrompt || image.render_prompt || image.revised_prompt || '';
    const promptModalText = buildRenderedPromptYamlText(generationPromptText);
    const copyPrompt = async () => {
        const request = ++promptCopyRequest.current.version;
        setPromptCopyState('pending');
        try {
            await navigator.clipboard.writeText(promptModalText);
            if (promptCopyRequest.current.version === request) setPromptCopyState('copied');
        } catch {
            if (promptCopyRequest.current.version === request) setPromptCopyState('error');
        }
    };
    const publicControl = metaPublicMetadata?.control && metaPublicMetadata.control !== '--' ? metaPublicMetadata.control : null;
    const publicPrompt = metaPublicMetadata?.prompt && metaPublicMetadata.prompt !== '--' ? metaPublicMetadata.prompt : null;
    const publicTitle = metaPublicMetadata?.title && metaPublicMetadata.title !== '--' ? metaPublicMetadata.title : null;
    const publicModelName = formatModelWithQuality(metaPublicMetadata?.model) || formatModelWithQuality(metaModelName);
    const publicQuality = metaPublicMetadata?.quality?.trim() && metaPublicMetadata.quality.trim() !== '--'
        ? metaPublicMetadata.quality.trim()
        : null;
    const publicModelDisplay = formatModelWithQuality(metaPublicMetadata?.model_display)
        || formatModelWithQuality(publicModelName, publicQuality);
    const modelDisplay = publicModelName ? (
        metaPublishVersion >= 1 && metaPublishModelName && metaPublishModelName !== metaModelName ? (
            <span className="inline-flex items-center gap-2">
                <span>{publicModelDisplay || publicModelName}</span>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4 text-blue-500 dark:text-blue-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
                <span>{metaPublishModelName}</span>
            </span>
        ) : (
            publicModelDisplay || publicModelName
        )
    ) : (
        '-'
    );
    const rating = metaPublicMetadata && 'rating' in metaPublicMetadata ? metaPublicMetadata.rating : image.rating;
    const validRating = typeof rating === 'number' && Number.isInteger(rating) && rating >= 1 && rating <= 5 ? rating : null;
    const ratingStars = '★'.repeat(validRating || 0) + '☆'.repeat(5 - (validRating || 0));
    const metadataEntries: { key: string; label: string; value: React.ReactNode }[] = [
        { key: 'rating', label: t('search.group.rating'), value: <>
            {validRating ? <a data-rating-stars className="underline" aria-label={t('detail.ratingValue', { rating: validRating })} href={`/?page=search&rating=${validRating}`}>{ratingStars}</a> : <span data-rating-stars aria-label={t('detail.unrated')}>{ratingStars} {t('detail.unrated')}</span>}
            {' · '}<button type="button" className="underline" onClick={() => openGuideHelp('ratings')}>{t('detail.ratingGuide')}</button>
            <BookmarkCount imageId={image.id} />
        </> },
        { key: 'id', label: content.ui[language].imageDetail.id, value: image.id },
        { key: 'timestamp', label: content.ui[language].imageDetail.timestamp, value: formatDateTime(image.time) },
        { key: 'model', label: content.ui[language].imageDetail.modelName, value: modelDisplay },
        ...(publicQuality ? [{ key: 'quality', label: t('detail.quality'), value: publicQuality }] : []),
        ...(publicControl ? [{ key: 'control', label: t('detail.control'), value: publicControl }] : []),
        ...(publicPrompt ? [{ key: 'prompt', label: t('detail.prompt'), value: publicPrompt }] : []),
        ...(publicTitle ? [{ key: 'title', label: t('detail.sourceTitle'), value: publicTitle }] : []),
    ];
    const majorControlEntries = Object.entries(metaPublicMetadata?.facets || image.facets || {})
        .map(([key, facet]) => ({ key, label: content.ui[language].search.group[key as keyof typeof content.ui.jp.search.group] || facet.group_label, value: facet.label, slug: facet.slug }));
    const controlLabels = content.ui[language].workflowControls;
    const publicControls = metaPublicMetadata?.major_workflow_controls;
    const controlOrder = Object.keys(controlLabels);
    const workflowControls = publicControls && typeof publicControls === 'object' && !Array.isArray(publicControls)
        ? Object.entries(publicControls).sort(([a], [b]) => {
            const ai = controlOrder.indexOf(a), bi = controlOrder.indexOf(b);
            return (ai < 0 ? controlOrder.length : ai) - (bi < 0 ? controlOrder.length : bi) || a.localeCompare(b);
        }) : [];
    const paletteColors = Array.isArray(image.color_palette)
        ? image.color_palette
            .slice(0, 6)
            .map((color, index) => ({
                key: `${color.hex || color.bucket || index}`,
                hex: color.hex || color.bucket_hex || '#000000',
                label: color.bucket_label || color.bucket || color.hex || '',
                searchKey: color.bucket || ''
            }))
            .filter(color => Boolean(color.hex))
        : [];
    const colorIndexCount = Array.isArray(image.color_index)
        ? image.color_index.filter(color => color?.key).length
        : 0;
    const isColorIndexIncomplete = colorIndexCount > 0 && colorIndexCount < 6;

    const palettePanel = paletteColors.length > 0 ? (
        <div className="mx-auto mb-4 max-w-3xl rounded-lg border border-gray-200 bg-white/80 p-3 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800/80 ivory:border-coffee-200 ivory:bg-coffee-50">
            <h2 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200 ivory:text-coffee-700">
                {t('detail.text2')}
            </h2>
            <div className="lab-detail-palette"><div className="lab-detail-palette-row">
                {paletteColors.map((color, index) => (
                    <a
                        key={color.key}
                        href={color.searchKey ? `/?page=color&color=${encodeURIComponent(color.searchKey)}` : undefined}
                        className="rounded-md border border-white/80 shadow ring-1 ring-black/10"
                        data-primary={(isColorIndexIncomplete && index === 0) || undefined}
                        style={{ backgroundColor: color.hex }}
                        title={t('detail.colorSearch', { color: color.label || color.hex })}
                        aria-label={t('detail.colorSearch', { color: color.label || color.hex })}
                    />
                ))}
            </div></div>
        </div>
    ) : null;

    return (
        <>
            {modalNavigation}
            <div className="lab-page lab-detail lab-content-page" data-detail-id={routeImageId}>
                {isPage ? <ContentPageHeader sidebar title={hasLongTitle ? t('detail.name', { id: image.id }) : displayName}>
                    <nav className="lab-image-navigation" aria-label={t('imageDetail.title')}>
                        <button className="lab-button lab-button-primary" type="button" onClick={handleBackToList}>{t('imageDetail.backToList')}</button>
                        {navigationCards}
                    </nav>
                </ContentPageHeader> : null}
                <div className="lg:flex lg:gap-6">
                    <div className="min-w-0 flex-1 text-gray-900 dark:text-gray-100 ivory:text-coffee-800">
                        <div className="lab-detail-columns">
                        <div className="lab-detail-art">
                        <DetailImage identity={imageHash} thumb={new URL(image.thumb, apiOrigin).href} src={imageUrl} title={displayName} model={publicModelDisplay} retry={attempt}
                            onOpen={() => setIsMagnifierOpen(true)} onFailure={() => setImageFailed(true)} />

                        {imageFailed && <p role="alert" className="lab-error">{t('common.imageFetchError')}<button className="lab-button" onClick={() => setAttempt(value => value + 1)}>{t('common.retry')}</button></p>}

                        </div>
                        <DetailRegion className="lab-detail-info" pending={false} fallback={360}>
                            <div className="lab-detail-actions">
                                <FavoriteControls key={image.id} image={{ id: image.id, hash: image.hash, year, month }} thumb={new URL(image.thumb, apiOrigin).href} />
                                <button type="button" className="lab-button" onClick={() => setIsShareOpen(true)}><Share2 aria-hidden="true" />{t('detail.text3')}</button>
                            </div>
                            <button type="button" className="lab-button lab-detail-parameters" onClick={() => setDetailPanel('data')}>{t('detail.data')}</button>
                            {palettePanel}
                            <p className="mb-4">{metadataEntries[0].label}: {metadataEntries[0].value}</p>
                            {majorControlEntries.length > 0 && (
                                <div className="mt-4">
                                    <h3 className="text-lg font-semibold mb-2">{t('detail.attributes')}</h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {majorControlEntries.map(({ key, label, value, slug }) => (
                                            <div
                                                key={key}
                                                className="rounded-md border border-gray-200 dark:border-gray-600 ivory:border-coffee-200 bg-white/70 dark:bg-gray-800/70 ivory:bg-coffee-50 px-3 py-2"
                                            >
                                                <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 ivory:text-coffee-500">
                                                    {label}
                                                </div>
                                                <div className="mt-1 text-sm text-gray-800 dark:text-gray-100 ivory:text-coffee-800 break-words">
                                                    <a className="underline" href={`/?page=search&${encodeURIComponent(key)}=${encodeURIComponent(slug)}`}>{value}</a>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {resolvedWord && (
                                <div className="mt-4 flex flex-wrap items-center gap-3">
                                    <span className="text-xs text-gray-500 dark:text-gray-300">
                                        {content.ui[language].tagSearch.selectedPrefix} {resolvedWord}
                                    </span>
                                </div>
                            )}

                        </DetailRegion>
                        </div>
                            <DetailRegion className="lab-detail-tags" pending={tagsPending} fallback={64}>
                            {tags.length > 0 && (
                                <section className="lab-detail-tags mt-4" aria-label={content.ui[language].imageDetail.tags}>
                                    <div className="flex flex-wrap gap-2 items-center">
                                        {(() => {
                                            const counts = tags.map(t => t.count);
                                            const minCount = Math.min(...counts);
                                            const maxCount = Math.max(...counts);
                                            const range = maxCount - minCount;

                                            return tags.map(tag => {
                                                let normalizedScore = 0;
                                                if (range > 0) {
                                                    normalizedScore = (tag.count - minCount) / range;
                                                }

                                                let styleClass = 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-sm';
                                                let fontClass = '';

                                                if (normalizedScore >= 0.8) {
                                                    styleClass = 'bg-purple-200 dark:bg-purple-800 text-purple-900 dark:text-purple-100 text-lg px-4 py-2 shadow-md';
                                                    fontClass = 'font-serif font-bold';
                                                } else if (normalizedScore >= 0.5) {
                                                    styleClass = 'bg-orange-100 dark:bg-orange-900 text-orange-900 dark:text-orange-100 text-base px-3 py-1.5 shadow-sm';
                                                    fontClass = 'font-semibold';
                                                } else if (normalizedScore >= 0.2) {
                                                    styleClass = 'bg-yellow-100 dark:bg-yellow-900 text-yellow-900 dark:text-yellow-100 text-sm px-3 py-1';
                                                }

                                                // Highlight current word
                                                if (resolvedWord === tag.word) {
                                                    styleClass = 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 font-bold border-2 border-green-500 px-3 py-1';
                                                }

                                                // Highlight hovered word
                                                if (hoveredWord === tag.word) {
                                                    styleClass += ' ring-2 ring-red-500 dark:ring-red-400 scale-110';
                                                }

                                                return (
                                                    <a
                                                        key={tag.word}
                                                        href={`/?page=word&word=${encodeURIComponent(tag.word)}`}
                                                        className={`rounded-full transition-all flex items-center ${styleClass} ${fontClass} hover:opacity-80`}
                                                        onClick={() => recordTagUse(tag.word, 8)}
                                                        onMouseEnter={(e) => {
                                                            setHoveredWord(tag.word);
                                                            showTooltip(content.tooltips[language].imageDetail.tag, e);
                                                        }}
                                                        onMouseLeave={() => {
                                                            setHoveredWord(null);
                                                            hideTooltip();
                                                        }}
                                                    >
                                                        {resolvedWord === tag.word && (
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                        # {tag.word} <span className="ml-1 opacity-70 text-[0.8em]">({tag.count})</span>
                                                    </a>
                                                );
                                            });
                                        })()}
                                    </div>
                                </section>
                            )}
                            </DetailRegion>




                        <DetailRegion className="lab-detail-related" pending={false} fallback={100}><RelatedImageLinks facets={metaPublicMetadata?.facets || image.facets || {}} imageId={image.id} /></DetailRegion>
                    </div>
                </div>
            </div>
            {isMagnifierOpen && (
                <Magnifier
                    imageUrl={highResImageUrl}
                    onClose={() => setIsMagnifierOpen(false)}
                    imageId={image.id.toString()}
                />
            )}
            <Modal
                isOpen={detailPanel !== null}
                helpSection={detailPanel === 'prompt' ? 'image-prompt' : 'image-data'}
                onClose={() => setDetailPanel(detailPanel === 'prompt' ? 'data' : null)}
                title={t(detailPanel === 'prompt' ? 'detail.text5' : 'detail.parameters')}
                panelClassName="max-w-5xl"
            >
                <div className="p-6 text-gray-900 dark:text-gray-100 ivory:text-coffee-800">
                    {detailPanel === 'data' ? <>
                            {(hasLongTitle || !isPage) && <p className="mb-4 break-words"><span className="lab-label">{t('detail.fullTitle')}</span><br />{displayName}</p>}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {metadataEntries.filter(entry => entry.key !== 'rating').map(({ key, label, value }) => (
                                    <div
                                        key={key}
                                        className="rounded-md border border-blue-200 dark:border-blue-700 ivory:border-blue-300 bg-blue-50/80 dark:bg-blue-950/30 ivory:bg-blue-50 px-3 py-2"
                                    >
                                        <div className="text-xs uppercase tracking-wide text-blue-600 dark:text-blue-300 ivory:text-blue-700">
                                            {label}
                                        </div>
                                        <div className="mt-1 text-sm text-blue-950 dark:text-blue-50 ivory:text-blue-950 break-words">
                                            {value}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <section className="lab-workflow-controls mt-4">
                                <h3 className="font-bold mb-2">{t('detail.workflowControls')}</h3>
                                {workflowControls.length ? <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {workflowControls.map(([key, value]) => <div key={key} data-control-key={key} className="min-w-0 rounded-md border p-3">
                                        <dt className="lab-label">{Object.prototype.hasOwnProperty.call(controlLabels, key) ? controlLabels[key as keyof typeof controlLabels] : key}</dt>
                                        <dd className="whitespace-pre-wrap break-words" style={{ overflowWrap: 'anywhere' }}>{typeof value === 'string' && value.trim() ? value : '--'}</dd>
                                    </div>)}
                                </dl> : <p className="lab-label">{t('detail.noWorkflowControls')}</p>}
                            </section>
                            <div className="mt-4 rounded-md border border-emerald-200 dark:border-emerald-800 ivory:border-green-300 bg-emerald-50/80 dark:bg-emerald-950/20 ivory:bg-green-50 px-3 py-2 text-sm text-emerald-900 dark:text-emerald-100 ivory:text-green-950">
                                {provenanceNotice}
                            </div>

                            {generationPromptText && (
                                <div className="mt-4">
                                    <motion.button
                                        whileHover={{ scale: 1.03 }}
                                        whileTap={{ scale: 0.97 }}
                                        ref={promptButton}
                                        onClick={() => setDetailPanel('prompt')}
                                        className="lab-button"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6M7 4h7l3 3v13H7z" />
                                        </svg>
                                        {t('detail.text4')}
                                    </motion.button>
                                </div>
                            )}

                    </> : <>
                    <div className="flex items-center gap-3 mb-3">
                        <button type="button" className="lab-button" disabled={promptCopyState === 'pending'} onClick={copyPrompt} aria-label={t('detail.copyPrompt')} title={t('detail.copyPrompt')}><Copy aria-hidden="true" /></button>
                        <span role="status">{promptCopyState === 'copied' ? t('detail.text8') : promptCopyState === 'pending' ? t('detail.copyingPrompt') : ''}</span>
                    </div>
                    {promptCopyState === 'error' && <p role="alert" className="lab-error">{t('detail.copyPromptError')}</p>}
                    <pre ref={promptContent} tabIndex={-1} className="max-h-[65vh] overflow-auto rounded-lg bg-gray-950 text-gray-100 p-4 text-xs leading-relaxed whitespace-pre-wrap break-words select-text">
                        {renderPromptYamlText(promptModalText)}
                    </pre>
                    </>}
                </div>
            </Modal>
            <ShareModal isOpen={isShareOpen} onClose={() => setIsShareOpen(false)} url={shareUrl} />
        </>
    );
};

const ImageDetailPage: React.FC<ImageDetailPageProps> = props => <ImageDetailContent {...props}
    imageId={new URLSearchParams(window.location.search).get('id') || ''} contextSearch={window.location.search} />;

export default ImageDetailPage;

